import type { AnalyticsDomainSnapshot, AnalyticsTrendPoint } from '~/types/leLens';

import { metricSparklineSeries } from '../catalogue/commercial/MetricStrip';

import type { AreaId } from './areaContract';

export type AreaSparkline = { values: Array<number | null>; smoothWindow: number };

type SeriesPoint = { value?: number | null };
type SnapshotWithSeries = { trend?: { series?: Record<string, SeriesPoint[]> } };

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/** Growth series arrive daily; a week of smoothing keeps the weekly cycle out of a 28px line. */
const GROWTH_SMOOTH_WINDOW = 7;
const GROWTH_SMOOTH_MIN_POINTS = 14;

/** "2026-10-15" or "2026-10-15T..." as "15 Oct 2026", or "15 Oct" without the year. Other text passes through. */
export function formatDay(value: string, withYear = true): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  const month = match ? MONTHS[Number(match[2]) - 1] : undefined;
  if (!match || !month) return value;
  return `${Number(match[3])} ${month}${withYear ? ` ${match[1]}` : ''}`;
}

const finite = (value: number | null | undefined) =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

/**
 * The series behind one tile's sparkline, from the snapshot Home already fetched. Commercial reads the
 * server's sparkline window; Growth reads its per-metric series. Loyalty carries no series, so its tiles
 * draw no line. An empty series draws nothing.
 */
export function areaSparkline(
  areaId: AreaId,
  metricId: string,
  snapshot: AnalyticsDomainSnapshot | null,
): AreaSparkline | null {
  if (!snapshot?.trend) return null;
  if (areaId === 'commercial') {
    const sparkline = snapshot.trend.sparkline;
    const points: AnalyticsTrendPoint[] = sparkline?.points ?? snapshot.trend.points.filter((point) => point.inPeriod);
    if (points.length === 0) return null;
    return metricSparklineSeries(
      points,
      metricId as Parameters<typeof metricSparklineSeries>[1],
      sparkline?.rollingWindowDays ?? 1,
    );
  }
  const series = (snapshot as unknown as SnapshotWithSeries).trend?.series?.[metricId];
  if (!series || series.length === 0) return null;
  return {
    smoothWindow: series.length > GROWTH_SMOOTH_MIN_POINTS ? GROWTH_SMOOTH_WINDOW : 1,
    values: series.map((point) => finite(point.value)),
  };
}
